import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@mdvip/shared/components/ui/combobox';
import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { cn } from '@mdvip/shared/lib/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMyPracticeMembers } from '@/hooks/members/useMyPracticeMembers';
import { filterMembers } from '@/lib/members/memberSearch';
import type { PracticeMember } from '@/lib/members/members.types';
import { useActiveMemberStore } from '@/stores/activeMemberStore';
import { MemberOptionRow } from './MemberOptionRow';

// Six 48px rows plus the list's 4px padding top and bottom (--spacing(74) = 296px), so the sixth row
// is whole before the scrollbar. The shared ComboboxList default (252px) shows five and a sliver.
const LIST_MAX_HEIGHT = 'max-h-[min(--spacing(74),calc(var(--available-height)---spacing(9)))]';

export interface MemberSwitcherProps {
  className?: string;
}

/**
 * The context bar's "switch to another patient" search — reuses the same roster query as the
 * landing modal (CNP-1174), so opening this costs nothing extra once that query has resolved once.
 *
 * Deliberately search-then-act rather than a persisted selection: picking a row calls
 * `setActiveMember` and clears the query immediately, so the box never shows the picked name — only
 * ever a live filter over the roster. See the ticket's Resolved Conflicts for why this departs from
 * the design mock's own 8-row cap and name-only filter.
 */
export function MemberSwitcher({ className }: MemberSwitcherProps) {
  const { t } = useTranslation('providers');
  const tError = useErrorTranslation();
  const { toast } = useToast();
  const { data: members, isPending, isError, refetch } = useMyPracticeMembers();
  const activeMemberId = useActiveMemberStore((state) => state.activeMemberId);
  const setActiveMember = useActiveMemberStore((state) => state.setActiveMember);

  const [query, setQuery] = useState('');
  // Takes over the live region from the filter-count announcement below until the next input
  // change — a plain derived value, not a second writer racing the same DOM node on the same tick.
  const [pickAnnouncement, setPickAnnouncement] = useState('');

  const isEmptyRoster = !isPending && !isError && (members?.length ?? 0) === 0;
  // Loading/empty-roster disable the input outright, which keeps Base UI's popup from ever opening.
  // Error deliberately does NOT disable it: Base UI never fires onOpenChange for a disabled control,
  // so a disabled input while erroring would make "reopening retries" (below) unreachable — the one
  // way out of the error would never fire. Content still shows nothing while erroring, via the
  // `results.length > 0` gate just below (no roster yet to filter).
  const disabled = isPending || isEmptyRoster;
  const results = filterMembers(members ?? [], query);
  // Zero matches (including "no roster loaded yet") is covered by not rendering ComboboxContent at
  // all, rather than forcing `open` false — the latter makes Base UI drop the query back to '' as
  // part of its own close handling, reopening onto the full, unfiltered roster.

  // Reports only for a failure the provider ran into by opening the switcher. Before a patient is
  // picked this control is mounted behind the landing window, which already shows and reports the
  // same failed load; reacting to the query's error state here would toast and log it a second time.
  const reportFailure = (failure: unknown) => {
    trackException(failure instanceof Error ? failure : new Error('myPracticeMembers failed'), {
      source: 'myPracticeMembers',
    });
    toast({ title: tError('title'), description: tError(extractGqlErrorCode(failure)), variant: 'destructive' });
  };

  // Reopening after a failed load retries once, rather than reopening onto the same stale error.
  const handleOpenChange = (open: boolean) => {
    if (open && isError) {
      refetch().then((result) => {
        if (result.isError) reportFailure(result.error);
      });
    }
  };

  const filterAnnouncement = query.trim() ? t('members.switcher.resultsCount', { count: results.length }) : '';
  const announcement = pickAnnouncement || filterAnnouncement;

  const handleInputValueChange = (value: string) => {
    // Base UI also calls this with '' right after a pick, as part of its own selection handling
    // (same follow-up call `itemToStringLabel` neuters for the input itself) — only genuine typing
    // (a non-empty value) should retire the "Now viewing" announcement `choose` just set.
    if (value) setPickAnnouncement('');
    setQuery(value);
  };

  const choose = (member: PracticeMember) => {
    setQuery('');
    if (member.id === activeMemberId) {
      setPickAnnouncement('');
      return;
    }
    setActiveMember(member.id);
    const name = [member.firstName, member.lastName].filter(Boolean).join(' ');
    setPickAnnouncement(t('members.switcher.nowViewing', { name }));
  };

  return (
    <div className={cn('w-full @3xl/mcb:w-80', className)}>
      <Combobox
        items={results}
        filter={null}
        inputValue={query}
        onInputValueChange={handleInputValueChange}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        itemToStringValue={(member: PracticeMember) => member.id}
        // Picking a row clears the box rather than filling it with the picked name (AC5) — Base UI
        // fills the input with this label as part of its own selection handling, so returning '' here
        // is what keeps it blank, rather than racing that fill with our own `choose`'s clear.
        itemToStringLabel={() => ''}
        onValueChange={(member: PracticeMember | null) => {
          if (member) choose(member);
        }}
      >
        <ComboboxInput
          placeholder={t('members.switcher.label')}
          aria-label={t('members.switcher.label')}
          showTrigger={false}
          // Leading search glyph, per the design — rendered as an absolutely-positioned overlay
          // (anchored to InputGroup's own `relative`) since ComboboxInput's addon slot is trailing
          // only; the descendant selector pads the actual text input so the glyph has room.
          className="[&_[data-slot=input-group-control]]:pl-9"
        >
          <Icon
            name="search"
            size="xs"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
        </ComboboxInput>
        {results.length > 0 && (
          <ComboboxContent>
            <ComboboxList aria-label={t('members.switcher.listLabel')} className={LIST_MAX_HEIGHT}>
              {(member: PracticeMember) => (
                <ComboboxItem key={member.id} value={member} className="min-h-11">
                  <MemberOptionRow member={member} selected={member.id === activeMemberId} />
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        )}
      </Combobox>
      <div className="sr-only" aria-live="polite" role="status">
        {announcement}
      </div>
    </div>
  );
}
